import { useEffect } from "react"

import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"

import { useAIParserSession } from "./use-ai-parser-session"
import { useEditTitleMutation } from "./use-edit-title-mutation"
import { createEditTitleSchema, type EditTitleFormData } from "../schemas/title-schema"

type UseEditTitleProps = {
  isOpen: boolean
  currentTitle: string
  sessionId: string | null
  onSuccess: () => void
}

export function useEditTitle({ isOpen, currentTitle, sessionId, onSuccess }: UseEditTitleProps) {
  const { t } = useTranslation()
  const editTitleMutation = useEditTitleMutation()
  const { currentSessionId, setCurrentSessionId } = useAIParserSession()

  const editTitleSchema = createEditTitleSchema(t)

  const form = useForm<EditTitleFormData>({
    resolver: zodResolver(editTitleSchema),
    defaultValues: {
      title: currentTitle,
    },
  })

  const editTitle = async (data: EditTitleFormData) => {
    await editTitleMutation.mutateAsync(
      {
        sessionId,
        title: data.title,
      },
      {
        onSuccess: (data) => {
          if (currentSessionId === null) {
            setCurrentSessionId(data.session_id)
          }
          onSuccess()
        },
      }
    )
  }

  useEffect(() => {
    form.reset({ title: currentTitle })
  }, [isOpen, currentTitle])

  return {
    form,
    editTitle,
    editTitleMutation,
  }
}
